// Schema-driven field renderer for the Group Insurance form — shared by the
// member form, the form-builder preview and the admin submission detail view.
// Field visibility (conditional logic) is evaluated via isVisible().
import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Star, Upload, X } from "lucide-react";

export function isVisible(field, answers) {
  const cond = field?.condition;
  if (!cond || !cond.key) return true;
  const val = answers?.[cond.key];
  const want = cond.value;
  switch (cond.op) {
    case "not_equals": return val !== want;
    case "in": return Array.isArray(want) ? want.includes(val) : val === want;
    default: return val === want;
  }
}

export function calcAge(dob) {
  if (!dob) return "";
  const d = new Date(dob);
  if (isNaN(d)) return "";
  const t = new Date();
  let a = t.getFullYear() - d.getFullYear();
  if (t.getMonth() < d.getMonth() || (t.getMonth() === d.getMonth() && t.getDate() < d.getDate())) a--;
  return a < 0 ? "" : a;
}

function FileInput({ value, onChange, accept, testid, image }) {
  const setFile = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert("File must be under 2 MB"); return; }
    const r = new FileReader();
    r.onload = () => onChange(r.result);
    r.readAsDataURL(file);
  };
  return (
    <div data-testid={testid}>
      {value ? (
        <div className="flex items-center gap-2">
          {image && <img src={value} alt="upload" className="h-14 rounded border" />}
          <span className="text-xs text-muted-foreground truncate max-w-[200px]">File attached</span>
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => onChange("")} data-testid={`${testid}-clear`}><X className="w-3.5 h-3.5" /></Button>
        </div>
      ) : (
        <label className="inline-flex items-center gap-2 rounded-md border border-dashed border-border px-3 py-2 text-sm cursor-pointer hover:bg-muted/50">
          <Upload className="w-4 h-4" /> Upload
          <input type="file" accept={accept} className="hidden" onChange={e => setFile(e.target.files?.[0])} data-testid={`${testid}-input`} />
        </label>
      )}
    </div>
  );
}

export default function FieldRenderer({ field, value, onChange, answers, readonly = false, testid }) {
  testid = testid || `gi-f-${field.key || field.id}`;
  const dis = readonly;

  // content-only blocks
  if (field.type === "heading") {
    return (
      <div className="pt-2" data-testid={testid}>
        <h4 className="text-base font-bold" style={{ fontFamily: "'Anek Malayalam', sans-serif" }}>{field.label}</h4>
        {field.description && <p className="text-xs text-muted-foreground mt-0.5">{field.description}</p>}
      </div>
    );
  }
  if (field.type === "description") {
    return <p className="text-sm text-muted-foreground whitespace-pre-wrap" data-testid={testid}>{field.label}</p>;
  }
  if (field.type === "divider") {
    return <Separator className="my-2" data-testid={testid} />;
  }

  const label = (
    <Label className="text-sm font-semibold">
      {field.label} {field.required && <span className="text-red-600">*</span>}
    </Label>
  );
  const desc = field.description ? <p className="text-xs text-muted-foreground">{field.description}</p> : null;

  let control = null;
  switch (field.type) {
    case "long_text":
      control = <Textarea rows={3} value={value || ""} placeholder={field.placeholder} disabled={dis} onChange={e => onChange(e.target.value)} data-testid={testid} />;
      break;
    case "number":
      control = <Input type="number" value={value ?? ""} placeholder={field.placeholder} min={field.min ?? undefined} max={field.max ?? undefined} step={field.step ?? undefined} disabled={dis} onChange={e => onChange(e.target.value)} data-testid={testid} />;
      break;
    case "email":
      control = <Input type="email" value={value || ""} placeholder={field.placeholder} disabled={dis} onChange={e => onChange(e.target.value)} data-testid={testid} />;
      break;
    case "mobile":
      control = <Input type="tel" value={value || ""} placeholder={field.placeholder || "10-digit mobile"} disabled={dis} onChange={e => onChange(e.target.value)} data-testid={testid} />;
      break;
    case "date":
      control = <Input type="date" value={value || ""} disabled={dis} onChange={e => onChange(e.target.value)} data-testid={testid} />;
      break;
    case "time":
      control = <Input type="time" value={value || ""} disabled={dis} onChange={e => onChange(e.target.value)} data-testid={testid} />;
      break;
    case "datetime":
      control = <Input type="datetime-local" value={value || ""} disabled={dis} onChange={e => onChange(e.target.value)} data-testid={testid} />;
      break;
    case "yes_no":
      control = (
        <RadioGroup value={value || ""} onValueChange={dis ? undefined : onChange} className="flex gap-4" data-testid={testid}>
          {["Yes", "No"].map(o => (
            <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
              <RadioGroupItem value={o} disabled={dis} data-testid={`${testid}-${o.toLowerCase()}`} /> {o}
            </label>
          ))}
        </RadioGroup>
      );
      break;
    case "radio":
      control = (
        <RadioGroup value={value || ""} onValueChange={dis ? undefined : onChange} className="space-y-1.5" data-testid={testid}>
          {(field.options || []).map(o => (
            <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
              <RadioGroupItem value={o} disabled={dis} data-testid={`${testid}-${String(o).replace(/\W+/g, "-").toLowerCase()}`} /> {o}
            </label>
          ))}
        </RadioGroup>
      );
      break;
    case "dropdown":
      control = (
        <Select value={value || ""} onValueChange={dis ? undefined : onChange} disabled={dis}>
          <SelectTrigger data-testid={testid}><SelectValue placeholder={field.placeholder || "Select…"} /></SelectTrigger>
          <SelectContent>
            {(field.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      );
      break;
    case "multi_select": {
      const arr = Array.isArray(value) ? value : [];
      control = (
        <div className="space-y-1.5" data-testid={testid}>
          {(field.options || []).map(o => (
            <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={arr.includes(o)} disabled={dis}
                onCheckedChange={v => onChange(v ? [...arr, o] : arr.filter(x => x !== o))}
                data-testid={`${testid}-${String(o).replace(/\W+/g, "-").toLowerCase()}`} /> {o}
            </label>
          ))}
        </div>
      );
      break;
    }
    case "checkbox":
      control = (
        <label className="flex items-start gap-2.5 cursor-pointer" data-testid={testid}>
          <Checkbox checked={!!value} disabled={dis} onCheckedChange={v => onChange(!!v)} className="mt-0.5" data-testid={`${testid}-box`} />
          <span className="text-sm leading-relaxed">{field.label} {field.required && <span className="text-red-600">*</span>}</span>
        </label>
      );
      return <div className="space-y-1.5">{control}{desc}</div>;
    case "rating": {
      const max = field.max || 5;
      control = (
        <div className="flex gap-1" data-testid={testid}>
          {Array.from({ length: max }, (_, i) => i + 1).map(n => (
            <button key={n} type="button" disabled={dis} onClick={() => onChange(n)}
              className="transition-transform hover:scale-110 disabled:opacity-60"
              data-testid={`${testid}-${n}`}>
              <Star className={`w-6 h-6 ${n <= (value || 0) ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`} />
            </button>
          ))}
        </div>
      );
      break;
    }
    case "slider": {
      const min = field.min ?? 0, max = field.max ?? 100, step = field.step ?? 1;
      control = (
        <div className="flex items-center gap-3" data-testid={testid}>
          <Slider value={[Number(value ?? field.default ?? min)]} min={min} max={max} step={step} disabled={dis}
            onValueChange={v => onChange(v[0])} className="flex-1" data-testid={`${testid}-slider`} />
          <span className="text-sm font-semibold w-14 text-right" data-testid={`${testid}-value`}>{value ?? field.default ?? min}</span>
        </div>
      );
      break;
    }
    case "file_upload":
      control = <FileInput value={value} onChange={onChange} testid={testid} />;
      break;
    case "image_upload":
      control = <FileInput value={value} onChange={onChange} accept="image/*" image testid={testid} />;
      break;
    case "signature":
      control = <FileInput value={value} onChange={onChange} accept="image/*" image testid={testid} />;
      break;
    case "table": {
      // columns from options; value = array of row arrays
      const cols = field.options || ["Column 1", "Column 2"];
      const rows = Array.isArray(value) && value.length ? value : [cols.map(() => "")];
      const setCell = (ri, ci, v) => onChange(rows.map((r, i) => i === ri ? r.map((c, j) => j === ci ? v : c) : r));
      control = (
        <div className="overflow-x-auto" data-testid={testid}>
          <table className="w-full text-sm border border-border rounded-md">
            <thead><tr className="bg-muted/50">{cols.map((c, ci) => <th key={ci} className="px-2 py-1.5 text-left text-xs font-semibold border-b border-border">{c}</th>)}</tr></thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri} className="border-b border-border/50">
                  {cols.map((_, ci) => (
                    <td key={ci} className="p-1">
                      <Input value={r[ci] || ""} disabled={dis} onChange={e => setCell(ri, ci, e.target.value)} className="h-8 text-xs border-0 bg-transparent" data-testid={`${testid}-${ri}-${ci}`} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {!dis && <Button type="button" variant="outline" size="sm" className="mt-2 h-7 text-xs" onClick={() => onChange([...rows, cols.map(() => "")])} data-testid={`${testid}-addrow`}>+ Row</Button>}
        </div>
      );
      break;
    }
    default:
      control = <Input value={value || ""} placeholder={field.placeholder} disabled={dis} onChange={e => onChange(e.target.value)} data-testid={testid} />;
  }

  return (
    <div className="space-y-1.5" data-testid={`${testid}-wrap`}>
      {label}
      {desc}
      {control}
    </div>
  );
}
